import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { Button, FormControl, FormHelperText, IconButton, InputAdornment, OutlinedInput } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { Form, Formik, type FormikHelpers } from 'formik';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup';
import { postUserLogin } from '../../utilities/api';

type Props = {
    onFormChange: (nextForm: 'forgot') => void;
};

export const LoginForm = ({ onFormChange }: Props) => {
    const navigate = useNavigate();
    const userLoginMutation = useMutation({ mutationFn: postUserLogin });
    const [showPassword, setShowPassword] = useState(false);
    const initialValues = { email: '', password: '' };
    const validationSchema = yup.object().shape({
        email: yup.string().email('Invalid email').required('Required'),
        password: yup.string().required('Required'),
    });

    const handleFormSubmit = async (values: typeof initialValues, { setErrors }: FormikHelpers<typeof initialValues>) => {
        userLoginMutation.mutate(values, {
            onSuccess: ({ token }) => {
                localStorage.setItem('auwx', token);
                navigate('/');
            },
            onError: () => {
                setErrors({ password: 'Email or password are invalid' });
            },
        });
    };

    const handleFormChange = () => onFormChange('forgot');

    const handleShowPassword = () => setShowPassword((shown) => !shown);

    return (
        <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleFormSubmit}>
            {({ values, errors, handleChange, handleBlur, touched, isSubmitting }) => (
                <Form noValidate autoComplete="off">
                    <FormControl fullWidth sx={{ mb: 2 }} error={Boolean(errors.email && touched.email)}>
                        <OutlinedInput
                            aria-describedby="email-text"
                            placeholder="Email"
                            type="email"
                            name="email"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.email}
                        />
                        {errors.email && touched.email && <FormHelperText id="email-text">{errors.email}</FormHelperText>}
                    </FormControl>
                    <FormControl fullWidth sx={{ mb: 2 }} error={Boolean(errors.password && touched.email)}>
                        <OutlinedInput
                            aria-describedby="password-text"
                            placeholder="Password"
                            type={showPassword ? 'text' : 'password'}
                            name="password"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.password}
                            endAdornment={
                                <InputAdornment position="end">
                                    <IconButton aria-label={showPassword ? 'hide password' : 'show password'} edge="end" onClick={handleShowPassword}>
                                        {showPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            }
                        />
                        {errors.password && touched.password && <FormHelperText id="password-text">{errors.password}</FormHelperText>}
                    </FormControl>
                    <Button variant="text" onClick={handleFormChange}>
                        Forgot password?
                    </Button>
                    <Button disabled={isSubmitting} type="submit" size="large" sx={{ mt: 2, mb: 2 }} fullWidth variant="contained">
                        Login
                    </Button>
                </Form>
            )}
        </Formik>
    );
};
